const CMS_BASE_URL =
  process.env.REACT_APP_CMS_URL ||
  process.env.NEXT_PUBLIC_CMS_URL ||
  "";

const CLOUDFRONT_BASE_URL =
  process.env.NEXT_PUBLIC_CLOUDFRONT_URL ||
  process.env.REACT_APP_NEXT_PUBLIC_CLOUDFRONT_URL ||
  "";

const DEFAULT_THUMBNAIL = `${CLOUDFRONT_BASE_URL.replace(/\/$/, "")}/media/default/assets/default-thumbnail.jpg`;

export const getDefaultThumbnail = () => DEFAULT_THUMBNAIL;

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
};

const decodeHtmlEntities = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
  }

  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

const stripHtml = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  return decodeHtmlEntities(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

const getRichBody = (attrs = {}) => {
  const rawBody =
    attrs?.body ||
    attrs?.content ||
    attrs?.description ||
    attrs?.teaser ||
    attrs?.excerpt ||
    attrs?.summary ||
    "";

  if (typeof rawBody !== "string") {
    return "";
  }

  return decodeHtmlEntities(rawBody).trim();
};

const toAbsoluteUrl = (url) => {
  if (!url) {
    return DEFAULT_THUMBNAIL;
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  if (!CMS_BASE_URL) {
    return DEFAULT_THUMBNAIL;
  }

  return `${CMS_BASE_URL.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
};

const getMediaUrl = (source, preferredFormats = ["medium", "small", "thumbnail"]) => {
  if (!source) {
    return DEFAULT_THUMBNAIL;
  }

  if (typeof source === "string") {
    return toAbsoluteUrl(source);
  }

  const media = source.data || source;
  const mediaUrl =
    media?.url ||
    preferredFormats.map((format) => media?.formats?.[format]?.url).find(Boolean);
  return toAbsoluteUrl(mediaUrl);
};

const normalizeArticle = (article) => {
  const attrs = article?.attributes || article;
  const category = attrs?.article_type || attrs?.sport;
  const imageSource =
    attrs?.thumbnail ||
    attrs?.image ||
    attrs?.cover ||
    attrs?.featuredImage ||
    attrs?.featured_image;

  return {
    id: article?.id || attrs?.id || crypto.randomUUID(),
    category:
      (typeof category === "string" ? category : category?.data?.attributes?.name || category?.name) ||
      "news",
    title: attrs?.title || attrs?.headline || "Untitled article",
    teaser: stripHtml(attrs?.teaser || attrs?.excerpt || attrs?.summary || attrs?.description || ""),
    image: getMediaUrl(imageSource, ["thumbnail", "small", "medium"]),
    date: formatDate(attrs?.publishedAt || attrs?.createdAt || attrs?.updatedAt),
    slug: attrs?.slug || "",
  };
};

const normalizeTravelArticle = (article) => {
  const attrs = article?.attributes || article;
  const category = attrs?.category || attrs?.theme?.name || attrs?.theme?.data?.attributes?.name;
  const imageSource =
    attrs?.thumbnail ||
    attrs?.image ||
    attrs?.cover ||
    attrs?.featuredImage ||
    attrs?.featured_image;

  const countryName =
    attrs?.country?.name ||
    attrs?.country?.data?.attributes?.name ||
    attrs?.location ||
    attrs?.region ||
    "";

  return {
    id: article?.id || attrs?.id || crypto.randomUUID(),
    category: (typeof category === "string" ? category : "Travel") || "Travel",
    title: attrs?.title || "Untitled travel article",
    teaser: stripHtml(attrs?.teaser || attrs?.excerpt || attrs?.summary || attrs?.description || ""),
    image: getMediaUrl(imageSource),
    date: formatDate(attrs?.publishedAt || attrs?.createdAt || attrs?.updatedAt),
    slug: attrs?.slug || "",
    location: countryName,
  };
};

const normalizeSportNewsArticle = (article) => {
  const attrs = article?.attributes || article;
  const category = attrs?.category;
  const imageSource =
    attrs?.featured_image ||
    attrs?.thumbnail ||
    attrs?.image ||
    attrs?.cover ||
    attrs?.featuredImage ||
    attrs?.featured_image;

  return {
    id: article?.id || attrs?.id || crypto.randomUUID(),
    category:
      (typeof category === "string" ? category : category?.data?.attributes?.name || category?.name) ||
      "Sports News",
    title: attrs?.title || attrs?.headline || "Untitled sport news",
    teaser: stripHtml(attrs?.excerpt || attrs?.teaser || attrs?.summary || attrs?.description || ""),
    image: attrs?.image_url || getMediaUrl(imageSource),
    date: formatDate(attrs?.publishedAt || attrs?.pubDate || attrs?.createdAt || attrs?.updatedAt),
    publishedDate: attrs?.published_date || attrs?.publishedAt || attrs?.pubDate || "",
    slug: attrs?.slug || "",
    sourceUrl: attrs?.source_url || "",
  };
};

const normalizeInterviewArticle = (article) => {
  const attrs = article?.attributes || article;
  const type = attrs?.type || attrs?.interviewType || attrs?.category;
  const imageSource =
    attrs?.thumbnail_image ||
    attrs?.image ||
    attrs?.cover ||
    attrs?.featuredImage ||
    attrs?.featured_image;

  return {
    id: article?.id || attrs?.id || crypto.randomUUID(),
    type: (typeof type === "string" ? type : type?.name || type?.data?.attributes?.name) || "Interview",
    name: attrs?.name || attrs?.guestName || attrs?.interviewee || attrs?.author || "Guest",
    role: attrs?.role || attrs?.designation || attrs?.titleRole || "",
    title: attrs?.title || attrs?.headline || "Untitled interview",
    teaser: stripHtml(attrs?.teaser || attrs?.excerpt || attrs?.summary || attrs?.description || ""),
    image: getMediaUrl(imageSource),
    date: formatDate(attrs?.publishedAt || attrs?.createdAt || attrs?.updatedAt),
    slug: attrs?.slug || "",
  };
};

const extractYouTubeVideoId = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "object") {
    return (
      extractYouTubeVideoId(value.videoId) ||
      extractYouTubeVideoId(value.id) ||
      extractYouTubeVideoId(value.resourceId) ||
      extractYouTubeVideoId(value.video)
    );
  }

  if (typeof value !== "string") {
    return "";
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return "";
  }

  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmedValue)) {
    return trimmedValue;
  }

  try {
    const parsedUrl = new URL(trimmedValue);

    if (parsedUrl.hostname.includes("youtu.be")) {
      return parsedUrl.pathname.replace(/\//g, "").slice(0, 11);
    }

    if (parsedUrl.pathname.includes("/shorts/")) {
      return parsedUrl.pathname.split("/shorts/")[1]?.split(/[/?&#]/)[0] || "";
    }

    if (parsedUrl.pathname.includes("/embed/")) {
      return parsedUrl.pathname.split("/embed/")[1]?.split(/[/?&#]/)[0] || "";
    }

    return parsedUrl.searchParams.get("v") || "";
  } catch (_error) {
    return "";
  }
};

const buildYouTubeWatchUrl = (videoId) => {
  if (!videoId) {
    return "";
  }

  return `https://www.youtube.com/watch?v=${videoId}`;
};

const buildYouTubeEmbedUrl = (videoId) => {
  if (!videoId) {
    return "";
  }

  return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
};

const getYouTubeThumbnailUrl = (video) => {
  const attrs = video?.attributes || video;
  const snippet = attrs?.snippet || attrs?.video?.snippet || attrs?.youtube?.snippet || {};
  const imageSource =
    attrs?.thumbnail ||
    attrs?.thumbnailUrl ||
    attrs?.image ||
    attrs?.cover ||
    snippet?.thumbnails?.maxres?.url ||
    snippet?.thumbnails?.standard?.url ||
    snippet?.thumbnails?.high?.url ||
    snippet?.thumbnails?.medium?.url ||
    snippet?.thumbnails?.default?.url;
  const videoId = extractYouTubeVideoId(
    attrs?.videoId ||
      attrs?.youtubeVideoId ||
      attrs?.youtube_id ||
      attrs?.ytVideoId ||
      attrs?.resourceId ||
      attrs?.id ||
      attrs?.youtubeUrl ||
      attrs?.videoUrl ||
      attrs?.url ||
      attrs?.link ||
      attrs?.embedUrl
  );

  if (imageSource) {
    return typeof imageSource === "string" ? toAbsoluteUrl(imageSource) : getMediaUrl(imageSource);
  }

  if (videoId) {
    return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  }

  return DEFAULT_THUMBNAIL;
};

const normalizeInterviewVideo = (video) => {
  const attrs = video?.attributes || video;
  const snippet = attrs?.snippet || attrs?.video?.snippet || attrs?.youtube?.snippet || {};
  const videoId = extractYouTubeVideoId(
    attrs?.videoId ||
      attrs?.youtubeVideoId ||
      attrs?.youtube_id ||
      attrs?.ytVideoId ||
      attrs?.resourceId ||
      attrs?.id ||
      attrs?.youtubeUrl ||
      attrs?.videoUrl ||
      attrs?.url ||
      attrs?.link ||
      attrs?.embedUrl
  );
  const title = decodeHtmlEntities(
    attrs?.title || attrs?.headline || snippet?.title || "Untitled video interview"
  ).trim();
  const description = stripHtml(
    attrs?.description || snippet?.description || attrs?.teaser || attrs?.excerpt || attrs?.summary || ""
  );
  const fallbackUrl = attrs?.youtubeUrl || attrs?.videoUrl || attrs?.url || attrs?.link || "";

  return {
    id: video?.id || attrs?.id || attrs?.documentId || videoId || crypto.randomUUID(),
    title,
    teaser: description,
    description,
    image: getYouTubeThumbnailUrl(video),
    date: formatDate(attrs?.publishedAt || snippet?.publishedAt || attrs?.createdAt || attrs?.updatedAt),
    publishedDate: attrs?.publishedAt || snippet?.publishedAt || attrs?.createdAt || attrs?.updatedAt || "",
    channelTitle: decodeHtmlEntities(attrs?.channelTitle || snippet?.channelTitle || attrs?.channel || "CynergySports"),
    videoId,
    watchUrl: videoId ? buildYouTubeWatchUrl(videoId) : fallbackUrl,
    embedUrl: videoId ? buildYouTubeEmbedUrl(videoId) : attrs?.embedUrl || "",
  };
};

const normalizeClubArticle = (article) => {
  const attrs = article?.attributes || article;
  const country = attrs?.country;
  const city = attrs?.city;
  const imageSource =
    attrs?.thumbnail ||
    attrs?.image ||
    attrs?.cover ||
    attrs?.featuredImage ||
    attrs?.featured_image;

  // Convert attrs?.short_description to HTML. If it ends with "Website: {url}",
  // move it to the next line and render it as a clickable link.
  let description = "";
  const shortDescriptionRaw = attrs?.short_description;
  const shortDescription = typeof shortDescriptionRaw === "string" ? shortDescriptionRaw.trim() : "";
  const websiteRegex = /Website:\s*(https?:\/\/[^\s]+)\s*$/i;
  const websiteMatch = shortDescription.match(websiteRegex);
  if (websiteMatch) {
    const [, url] = websiteMatch;
    description = shortDescription.replace(
      websiteRegex,
      `<br><a href="${url}" target="_blank" rel="noopener noreferrer" class="text-amber-dusk hover:text-solar-orange transition-colors">Visit Website</a>`
    );
  }else if (attrs?.website_url) {
    description = shortDescription + `<br><a href="${attrs.website_url}" target="_blank" rel="noopener noreferrer" class="text-amber-dusk hover:text-solar-orange transition-colors">Visit Website</a>`;
  }

  return {
    id: article?.id || attrs?.id || crypto.randomUUID(),
    name: attrs?.name || attrs?.title || attrs?.headline || "Untitled club",
    country:
      (typeof country === "string" ? country : country?.name || country?.data?.attributes?.name) ||
      "Unknown",
    city: (typeof city === "string" ? city : city?.name || city?.data?.attributes?.name) || "",
    level: attrs?.level || attrs?.skillLevel || attrs?.programLevel || "All Levels",
    facility: attrs?.facility || attrs?.facilityType || attrs?.poolType || "Indoor",
    members: attrs?.members || attrs?.memberCount || attrs?.capacity || "",
    teaser: stripHtml(attrs?.teaser || attrs?.excerpt || attrs?.summary || attrs?.description || ""),
    short_description: description || stripHtml(attrs?.short_description || ""),
    image: getMediaUrl(imageSource),
    date: formatDate(attrs?.publishedAt || attrs?.createdAt || attrs?.updatedAt),
    slug: attrs?.slug || "",
  };
};

const normalizeMeetTags = (value) => {
  const values = Array.isArray(value) ? value : value ? [value] : [];

  return values
    .flatMap((item) => {
      if (!item) {
        return [];
      }

      if (typeof item === "string") {
        return [item.trim()];
      }

      const source = item?.attributes || item;
      const label =
        source?.name ||
        source?.title ||
        source?.label ||
        source?.slug ||
        source?.tag ||
        "";

      return label ? [String(label).trim()] : [];
    })
    .filter(Boolean)
    .filter((tag, index, tags) => tags.indexOf(tag) === index);
};

const normalizeTag = (tag) => {
  const attrs = tag?.attributes || tag || {};
  const name = attrs?.name || attrs?.title || attrs?.label || attrs?.tag || attrs?.slug || "";
  return {
    id: tag?.id || attrs?.id || crypto.randomUUID(),
    name: String(name).trim(),
    slug: attrs?.slug || String(name).toLowerCase().replace(/[^a-z0-9-_]+/g, "-").replace(/^-|-$/g, ""),
  };
};

export const fetchTags = async ({ limit = 200 } = {}) => {
  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const base = CMS_BASE_URL.replace(/\/$/, "");
  const endpoints = [
    `${base}/api/tags?populate=*&sort=name:asc&pagination[limit]=${limit}`,
    `${base}/api/tag?populate=*&sort=name:asc&pagination[limit]=${limit}`,
  ];

  const headers = createJsonHeaders();

  try {
    const payload = await fetchFromAnyEndpoint(endpoints, headers);
    const payloadData = Array.isArray(payload?.data) ? payload.data : payload?.data ? [payload.data] : [];
    const items = payloadData.map(normalizeTag).filter((t) => t.name).map((t) => t.name);
    return Array.from(new Set(items));
  } catch (_error) {
    return [];
  }
};

const normalizeMeetArticle = (article) => {
  const attrs = article?.attributes || article;
  const isArchived = attrs?.is_archived ?? attrs?.isArchived ?? false;
  // If this item came from the events endpoint or is categorized as event
  let inferredType = attrs?.type || attrs?.meetType || attrs?.category;
  
  let tName = typeof inferredType === "string" ? inferredType : inferredType?.name || inferredType?.data?.attributes?.name;
  if (!tName && (article?.isEventEntry || String(attrs?.slug || '').includes('event'))) {
    tName = "Event";
  }

  const type = tName || "Regional";
  
  const tags = normalizeMeetTags(
    attrs?.tags?.data || attrs?.tags || attrs?.tag?.data || attrs?.tag || attrs?.categories?.data || attrs?.categories
  );
  if (!tags.some(t => t.toLowerCase() === 'event') && (tName?.toLowerCase() === 'event' || article?.isEventEntry)) {
      tags.push('Event');
  }
  const clubName =
    attrs?.club?.name ||
    attrs?.club?.data?.attributes?.name ||
    attrs?.clubName ||
    "";
  const countryName =
    attrs?.country?.name ||
    attrs?.country?.data?.attributes?.name ||
    attrs?.country ||
    "";
  const startDate = attrs?.startDate || attrs?.start_date || attrs?.date;
  const endDate = attrs?.endDate || attrs?.end_date;
  const imageSource =
    attrs?.thumbnail ||
    attrs?.image ||
    attrs?.cover ||
    attrs?.featuredImage ||
    attrs?.featured_image;

  return {
    id: article?.id || attrs?.id || crypto.randomUUID(),
    name: attrs?.name || attrs?.title || attrs?.headline || "Untitled meet",
    location: attrs?.location || attrs?.venue || attrs?.city || attrs?.country || "TBA",
    startDate: startDate || attrs?.publishedAt || attrs?.createdAt || attrs?.updatedAt || "",
    date:
      attrs?.eventDateRange ||
      (startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : "") ||
      formatDate(startDate) ||
      formatDate(attrs?.publishedAt || attrs?.createdAt || attrs?.updatedAt),
    status: attrs?.status || "upcoming",
    type: type,
    participants: attrs?.participants || attrs?.athleteCount || attrs?.attendance || attrs?.participantCount || "",
    description: stripHtml(attrs?.description || attrs?.teaser || attrs?.excerpt || attrs?.summary || ""),
    image: getMediaUrl(imageSource),
    slug: attrs?.slug || "",
    country: countryName,
    club: clubName,
    tags,
    registrationUrl: attrs?.registration_url || attrs?.registrationUrl || "",
    isArchived,
  };
};

const fetchFromProxy = async (url, headers) => {
  const proxyUrl = `/api/proxy?url=${encodeURIComponent(url)}`;

  const fetchOptions = {
    headers: {
      ...headers,
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
    cache: "no-store",
  };

  let response = await fetch(proxyUrl, fetchOptions);

  // Some proxy/CDN layers answer with 304 and no body; retry once with a cache-busted URL.
  if (response.status === 304) {
    const bustedProxyUrl = `/api/proxy?url=${encodeURIComponent(url)}&t=${Date.now()}`;
    response = await fetch(bustedProxyUrl, fetchOptions);
  }

  if (!response.ok) {
    throw new Error(`Proxy request failed with status ${response.status}`);
  }

  return response.json();
};

const fetchDirect = async (url, headers) => {
  const response = await fetch(url, {
    headers: {
      ...headers,
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`Strapi request failed with status ${response.status}`);
  }

  return response.json();
};

const fetchDirectJson = async (url) => {
  const response = await fetch(url);
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(
      payload?.error ||
      payload?.message ||
      `Strapi request failed with status ${response.status}`
    );
  }

  return payload;
};

const createJsonHeaders = () => ({
  "Content-Type": "application/json",
});

const canUseDirectFetch = (endpoint) => {
  return Boolean(endpoint);
};

const fetchFromAnyEndpoint = async (endpoints, headers) => {
  for (const endpoint of endpoints) {
    try {
      return await fetchFromProxy(endpoint, headers);
    } catch (_proxyError) {
      if (canUseDirectFetch(endpoint)) {
        try {
          return await fetchDirect(endpoint, headers);
        } catch (_directError) {
          // Try next endpoint variant.
        }
      }
    }
  }

  throw new Error("Unable to load articles from CMS.");
};

const toPayloadItems = (payload) => {
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (payload?.data) {
    return [payload.data];
  }

  return [];
};

const fetchFromAllEndpoints = async (endpoints, headers) => {
  const items = [];

  for (const endpoint of endpoints) {
    try {
      let payload;

      try {
        payload = await fetchFromProxy(endpoint, headers);
      } catch (_proxyError) {
        if (!canUseDirectFetch(endpoint)) {
          continue;
        }

        payload = await fetchDirect(endpoint, headers);
      }

      const resItems = toPayloadItems(payload);
      if (endpoint.includes('/api/events')) {
          resItems.forEach(item => { item.isEventEntry = true; });
      }
      items.push(...resItems);
    } catch (_error) {
      // Ignore one endpoint failing and keep collecting the rest.
    }
  }

  if (!items.length) {
    throw new Error("Unable to load articles from CMS.");
  }

  return items;
};

export const fetchArticles = async ({ page = 1, pageSize, limit = 6 } = {}) => {
  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const normalizedPage = Math.max(1, Number(page) || 1);
  const normalizedPageSize = Math.max(1, Number(pageSize || limit) || 6);
  const base = CMS_BASE_URL.replace(/\/$/, "");
  const endpoints = [
    `${base}/api/sports-articles?populate=*&sort=publishedAt:desc&pagination[page]=${normalizedPage}&pagination[pageSize]=${normalizedPageSize}`,
    `${base}/api/articles?populate=*&sort=publishedAt:desc&pagination[page]=${normalizedPage}&pagination[pageSize]=${normalizedPageSize}`,
  ];

  const headers = createJsonHeaders();

  const payload = await fetchFromAnyEndpoint(endpoints, headers);
  const payloadData = Array.isArray(payload?.data) ? payload.data : payload?.data ? [payload.data] : [];
  const items = payloadData.map(normalizeArticle);
  const metaPagination = payload?.meta?.pagination || {};
  const total = Number(metaPagination?.total);
  const resolvedTotal = Number.isFinite(total) ? total : items.length;
  const metaPage = Number(metaPagination?.page);
  const resolvedPage = Number.isFinite(metaPage) && metaPage > 0 ? metaPage : normalizedPage;
  const metaPageSize = Number(metaPagination?.pageSize);
  const resolvedPageSize = Number.isFinite(metaPageSize) && metaPageSize > 0 ? metaPageSize : normalizedPageSize;
  const metaPageCount = Number(metaPagination?.pageCount);
  const resolvedPageCount =
    Number.isFinite(metaPageCount) && metaPageCount > 0
      ? metaPageCount
      : Math.max(1, Math.ceil(resolvedTotal / resolvedPageSize));

  return {
    items,
    pagination: {
      page: resolvedPage,
      pageSize: resolvedPageSize,
      pageCount: resolvedPageCount,
      total: resolvedTotal,
    },
  };
};

export const fetchArticleBySlug = async (slug) => {
  if (!slug) {
    throw new Error("Missing article slug.");
  }

  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const base = CMS_BASE_URL.replace(/\/$/, "");
  const encodedSlug = encodeURIComponent(slug);
  const endpoints = [
    `${base}/api/sports-articles?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
    `${base}/api/articles?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
  ];

  const headers = createJsonHeaders();

  const payload = await fetchFromAnyEndpoint(endpoints, headers);
  const first = payload?.data?.[0];
  if (!first) {
    throw new Error("Article not found.");
  }

  const normalized = normalizeArticle(first);
  const attrs = first?.attributes || first;

  return {
    ...normalized,
    body: getRichBody(attrs),
  };
};

export const fetchSportNews = async ({ page = 1, pageSize = 12 } = {}) => {
  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const normalizedPage = Math.max(1, Number(page) || 1);
  const normalizedPageSize = Math.max(1, Number(pageSize) || 12);
  const base = CMS_BASE_URL.replace(/\/$/, "");
  const endpoints = [
    `${base}/api/rss-feed-news?populate=*&sort=publishedAt:desc&pagination[page]=${normalizedPage}&pagination[pageSize]=${normalizedPageSize}`,
    `${base}/api/rss-feed-news-entries?populate=*&sort=published_date:desc&pagination[page]=${normalizedPage}&pagination[pageSize]=${normalizedPageSize}`,
    `${base}/api/rss-feed-news-items?populate=*&sort=published_date:desc&pagination[page]=${normalizedPage}&pagination[pageSize]=${normalizedPageSize}`,
    `${base}/api/rss-feed-newses?populate=*&sort=publishedAt:desc&pagination[page]=${normalizedPage}&pagination[pageSize]=${normalizedPageSize}`,
  ];

  const headers = createJsonHeaders();

  let payload;
  try {
    payload = await fetchFromAnyEndpoint(endpoints, headers);
  } catch (_error) {
    throw new Error(
      "RSS Feed News endpoint is not available in Strapi Content API. Please enable/find route for api::rss-feed-news.rss-feed-news."
    );
  }
  const payloadData = Array.isArray(payload?.data)
    ? payload.data
    : payload?.data
      ? [payload.data]
      : [];

  const items = payloadData.map(normalizeSportNewsArticle);
  const metaPagination = payload?.meta?.pagination || {};
  const total = Number(metaPagination?.total);
  const resolvedTotal = Number.isFinite(total) ? total : items.length;
  const metaPage = Number(metaPagination?.page);
  const resolvedPage = Number.isFinite(metaPage) && metaPage > 0 ? metaPage : normalizedPage;
  const metaPageSize = Number(metaPagination?.pageSize);
  const resolvedPageSize = Number.isFinite(metaPageSize) && metaPageSize > 0 ? metaPageSize : normalizedPageSize;
  const metaPageCount = Number(metaPagination?.pageCount);
  const resolvedPageCount =
    Number.isFinite(metaPageCount) && metaPageCount > 0
      ? metaPageCount
      : Math.max(1, Math.ceil(resolvedTotal / resolvedPageSize));

  return {
    items,
    pagination: {
      page: resolvedPage,
      pageSize: resolvedPageSize,
      pageCount: resolvedPageCount,
      total: resolvedTotal,
    },
  };
};

export const fetchSportNewsBySlug = async (slug) => {
  if (!slug) {
    throw new Error("Missing sport news slug.");
  }

  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const base = CMS_BASE_URL.replace(/\/$/, "");
  const encodedSlug = encodeURIComponent(slug);
  const isNumericId = /^\d+$/.test(String(slug));
  const endpoints = [
    `${base}/api/rss-feed-news?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[pageSize]=1`,
    `${base}/api/rss-feed-news-entries?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[pageSize]=1`,
    `${base}/api/rss-feed-news-items?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[pageSize]=1`,
    `${base}/api/rss-feed-newses?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[pageSize]=1`,
    `${base}/api/rss-feed-news?populate=*&filters[documentId][$eq]=${encodedSlug}&pagination[pageSize]=1`,
  ];

  if (isNumericId) {
    endpoints.push(`${base}/api/rss-feed-news?populate=*&filters[id][$eq]=${encodedSlug}&pagination[pageSize]=1`);
    endpoints.push(`${base}/api/rss-feed-news-entries/${slug}?populate=*`);
    endpoints.push(`${base}/api/rss-feed-news-items/${slug}?populate=*`);
    endpoints.push(`${base}/api/rss-feed-news/${slug}?populate=*`);
    endpoints.push(`${base}/api/rss-feed-newses/${slug}?populate=*`);
  }

  const headers = createJsonHeaders();

  let payload;
  try {
    payload = await fetchFromAnyEndpoint(endpoints, headers);
  } catch (_error) {
    throw new Error(
      "RSS Feed News endpoint is not available in Strapi Content API. Please enable/find route for api::rss-feed-news.rss-feed-news."
    );
  }
  const payloadData = Array.isArray(payload?.data) ? payload.data : payload?.data ? [payload.data] : [];
  const first = payloadData?.[0];

  if (!first) {
    throw new Error("Sport news article not found.");
  }

  const normalized = normalizeSportNewsArticle(first);
  const attrs = first?.attributes || first;

  return {
    ...normalized,
    body: getRichBody(attrs),
  };
};

export const fetchTravelArticles = async ({ page = 1, pageSize, limit = 6 } = {}) => {
  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const normalizedPage = Math.max(1, Number(page) || 1);
  const normalizedPageSize = Math.max(1, Number(pageSize || limit) || 6);
  const base = CMS_BASE_URL.replace(/\/$/, "");
  const endpoints = [
    `${base}/api/travel-articles?populate=*&sort=publishedAt:desc&pagination[page]=${normalizedPage}&pagination[pageSize]=${normalizedPageSize}`,
    // `${base}/api/articles?populate=*&filters[category][$containsi]=travel&sort=publishedAt:desc&pagination[page]=${normalizedPage}&pagination[pageSize]=${normalizedPageSize}`,
  ];

  const headers = createJsonHeaders();

  const payload = await fetchFromAnyEndpoint(endpoints, headers);
  const payloadData = Array.isArray(payload?.data) ? payload.data : payload?.data ? [payload.data] : [];
  const items = payloadData.map(normalizeTravelArticle);
  const metaPagination = payload?.meta?.pagination || {};
  const total = Number(metaPagination?.total);
  const resolvedTotal = Number.isFinite(total) ? total : items.length;
  const metaPage = Number(metaPagination?.page);
  const resolvedPage = Number.isFinite(metaPage) && metaPage > 0 ? metaPage : normalizedPage;
  const metaPageSize = Number(metaPagination?.pageSize);
  const resolvedPageSize = Number.isFinite(metaPageSize) && metaPageSize > 0 ? metaPageSize : normalizedPageSize;
  const metaPageCount = Number(metaPagination?.pageCount);
  const resolvedPageCount =
    Number.isFinite(metaPageCount) && metaPageCount > 0
      ? metaPageCount
      : Math.max(1, Math.ceil(resolvedTotal / resolvedPageSize));

  return {
    items,
    pagination: {
      page: resolvedPage,
      pageSize: resolvedPageSize,
      pageCount: resolvedPageCount,
      total: resolvedTotal,
    },
  };
};

export const fetchTravelArticleBySlug = async (slug) => {
  if (!slug) {
    throw new Error("Missing travel article slug.");
  }

  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const base = CMS_BASE_URL.replace(/\/$/, "");
  const encodedSlug = encodeURIComponent(slug);
  const endpoints = [
    `${base}/api/travel-articles?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
    `${base}/api/articles?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
  ];

  const headers = createJsonHeaders();

  const payload = await fetchFromAnyEndpoint(endpoints, headers);
  const first = payload?.data?.[0];
  if (!first) {
    throw new Error("Travel article not found.");
  }

  const normalized = normalizeTravelArticle(first);
  const attrs = first?.attributes || first;

  return {
    ...normalized,
    body: getRichBody(attrs),
  };
};

export const fetchInterviews = async ({ page = 1, pageSize, limit = 12 } = {}) => {
  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const normalizedPage = Math.max(1, Number(page) || 1);
  const normalizedPageSize = Math.max(1, Number(pageSize || limit) || 12);
  const base = CMS_BASE_URL.replace(/\/$/, "");
  const endpoints = [
    `${base}/api/interviews?populate=*&sort=publishedAt:desc&pagination[page]=${normalizedPage}&pagination[pageSize]=${normalizedPageSize}`,
    // `${base}/api/articles?populate=*&filters[category][$containsi]=interview&sort=publishedAt:desc&pagination[limit]=${limit}`,
  ];

  const headers = createJsonHeaders();

  const payload = await fetchFromAnyEndpoint(endpoints, headers);
  const payloadData = Array.isArray(payload?.data) ? payload.data : payload?.data ? [payload.data] : [];
  const items = payloadData.map(normalizeInterviewArticle);
  const metaPagination = payload?.meta?.pagination || {};
  const total = Number(metaPagination?.total);
  const resolvedTotal = Number.isFinite(total) ? total : items.length;
  const metaPage = Number(metaPagination?.page);
  const resolvedPage = Number.isFinite(metaPage) && metaPage > 0 ? metaPage : normalizedPage;
  const metaPageSize = Number(metaPagination?.pageSize);
  const resolvedPageSize = Number.isFinite(metaPageSize) && metaPageSize > 0 ? metaPageSize : normalizedPageSize;
  const metaPageCount = Number(metaPagination?.pageCount);
  const resolvedPageCount =
    Number.isFinite(metaPageCount) && metaPageCount > 0
      ? metaPageCount
      : Math.max(1, Math.ceil(resolvedTotal / resolvedPageSize));

  return {
    items,
    pagination: {
      page: resolvedPage,
      pageSize: resolvedPageSize,
      pageCount: resolvedPageCount,
      total: resolvedTotal,
    },
  };
};

export const fetchInterviewVideos = async ({ channel = "@cynergysports", maxResults = 12, pageToken = "" } = {}) => {
  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const base = CMS_BASE_URL.replace(/\/$/, "");
  const normalizedMaxResults = Math.max(1, Number(maxResults) || 12);
  const endpoint =
    `${base}/api/interviews/youtube/videos` +
    `?channel=${encodeURIComponent(channel)}&maxResults=${normalizedMaxResults}` +
    (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : "");

  const payload = await fetchDirectJson(endpoint);
  const resolvedItems =
    payload?.data?.items ||
    payload?.data?.videos ||
    payload?.data ||
    payload?.items ||
    payload?.videos ||
    payload;
  const items = Array.isArray(resolvedItems) ? resolvedItems : [];

  return {
    items: items
      .map(normalizeInterviewVideo)
      .filter((video) => Boolean(video.videoId || video.embedUrl)),
    nextPageToken: payload?.data?.nextPageToken || payload?.nextPageToken || "",
    channelHandle: payload?.data?.channelHandle || payload?.channelHandle || channel,
    channelId: payload?.data?.channelId || payload?.channelId || "",
    maxResults: Number(payload?.data?.maxResults || payload?.maxResults || normalizedMaxResults),
  };
};

export const fetchInterviewBySlug = async (slug) => {
  if (!slug) {
    throw new Error("Missing interview slug.");
  }

  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const base = CMS_BASE_URL.replace(/\/$/, "");
  const encodedSlug = encodeURIComponent(slug);
  const endpoints = [
    `${base}/api/interviews?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
    `${base}/api/articles?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
  ];

  const headers = createJsonHeaders();

  const payload = await fetchFromAnyEndpoint(endpoints, headers);
  const first = payload?.data?.[0];
  if (!first) {
    throw new Error("Interview not found.");
  }

  const normalized = normalizeInterviewArticle(first);
  const attrs = first?.attributes || first;

  return {
    ...normalized,
    body: getRichBody(attrs),
  };
};

export const fetchClubs = async ({ limit = 24 } = {}) => {
  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const base = CMS_BASE_URL.replace(/\/$/, "");
  const endpoints = [
    `${base}/api/clubs?populate=*&sort=publishedAt:desc&pagination[limit]=${limit}`,
    // `${base}/api/articles?populate=*&filters[category][$containsi]=club&sort=publishedAt:desc&pagination[limit]=${limit}`,
  ];

  const headers = createJsonHeaders();

  const payload = await fetchFromAnyEndpoint(endpoints, headers);
  return (payload?.data || []).map(normalizeClubArticle);
};

export const fetchClubBySlug = async (slug) => {
  if (!slug) {
    throw new Error("Missing club slug.");
  }

  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const base = CMS_BASE_URL.replace(/\/$/, "");
  const encodedSlug = encodeURIComponent(slug);
  const endpoints = [
    `${base}/api/clubs?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
    // `${base}/api/articles?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
  ];

  const headers = createJsonHeaders();

  const payload = await fetchFromAnyEndpoint(endpoints, headers);
  const first = payload?.data?.[0];
  if (!first) {
    throw new Error("Club not found.");
  }

  const normalized = normalizeClubArticle(first);
  const attrs = first?.attributes || first;

  return {
    ...normalized,
    body: getRichBody(attrs),
  };
};

export const fetchMeets = async ({ limit = 24, country = "" } = {}) => {
  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const base = CMS_BASE_URL.replace(/\/$/, "");
  const normalizedCountry = String(country || "").trim();
  const countryFilter =
    normalizedCountry && normalizedCountry.toLowerCase() !== "all"
      ? `&filters[country][name][$eqi]=${encodeURIComponent(normalizedCountry)}`
      : "";
  const endpoints = [
    // `${base}/api/meets?populate=*&sort=publishedAt:desc&pagination[limit]=${limit}`,
    `${base}/api/club-meets?populate=*&sort=publishedAt:desc&pagination[limit]=${limit}${countryFilter}`,
    // `${base}/api/competition-meets?populate=*&sort=publishedAt:desc&pagination[limit]=${limit}`,
    // `${base}/api/swim-meets?populate=*&sort=publishedAt:desc&pagination[limit]=${limit}`,
    // `${base}/api/events?populate=*&sort=publishedAt:desc&pagination[limit]=${limit}`,
    // `${base}/api/articles?populate=*&filters[category][$containsi]=meet&sort=publishedAt:desc&pagination[limit]=${limit}`,
    // `${base}/api/articles?populate=*&filters[category][name][$containsi]=meet&sort=publishedAt:desc&pagination[limit]=${limit}`,
    // `${base}/api/articles?populate=*&filters[type][$containsi]=meet&sort=publishedAt:desc&pagination[limit]=${limit}`,
    // `${base}/api/articles?populate=*&filters[meetType][$containsi]=meet&sort=publishedAt:desc&pagination[limit]=${limit}`,
    // `${base}/api/articles?populate=*&filters[category][$containsi]=event&sort=publishedAt:desc&pagination[limit]=${limit}`,
    // `${base}/api/articles?populate=*&filters[category][$containsi]=competition&sort=publishedAt:desc&pagination[limit]=${limit}`,
    // `${base}/api/articles?populate=*&sort=publishedAt:desc&pagination[limit]=${limit}`,
  ];

  const headers = createJsonHeaders();

  try {
    const payloadItems = await fetchFromAllEndpoints(endpoints, headers);
    // console.log("payloadItems", payloadItems);
    const normalized = payloadItems
      .map(normalizeMeetArticle)
      .filter((meet) => !meet.isArchived)
      .filter((meet, index, meets) => {
        const dedupeKey = meet?.slug || meet?.id;
        return meets.findIndex((item) => (item?.slug || item?.id) === dedupeKey) === index;
      });

    // console.log("normalized", normalized);
    if (!normalized.length) {
      return [];
    }
    
    // console.log(normalized);
    return normalized;
  } catch (_error) {
    return [];
  }
};

export const fetchMeetBySlug = async (slug) => {
  if (!slug) {
    throw new Error("Missing meet slug.");
  }

  if (!CMS_BASE_URL) {
    throw new Error("Missing CMS base URL. Set REACT_APP_CMS_URL.");
  }

  const base = CMS_BASE_URL.replace(/\/$/, "");
  const encodedSlug = encodeURIComponent(slug);
  const isNumericId = /^\d+$/.test(String(slug));
  const endpoints = [
    `${base}/api/meets?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
    `${base}/api/club-meets?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
    `${base}/api/competition-meets?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
    `${base}/api/swim-meets?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
    `${base}/api/events?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
    `${base}/api/articles?populate=*&filters[slug][$eq]=${encodedSlug}&pagination[limit]=1`,
  ];

  if (isNumericId) {
    endpoints.push(`${base}/api/meets/${slug}?populate=*`);
    endpoints.push(`${base}/api/club-meets/${slug}?populate=*`);
    endpoints.push(`${base}/api/competition-meets/${slug}?populate=*`);
    endpoints.push(`${base}/api/swim-meets/${slug}?populate=*`);
    endpoints.push(`${base}/api/events/${slug}?populate=*`);
    endpoints.push(`${base}/api/articles/${slug}?populate=*`);
  }

  const headers = createJsonHeaders();

  const payload = await fetchFromAnyEndpoint(endpoints, headers);
  const payloadData = Array.isArray(payload?.data) ? payload.data : payload?.data ? [payload.data] : [];
  const first = payloadData?.[0];
  if (!first) {
    throw new Error("Meet not found.");
  }

  const normalized = normalizeMeetArticle(first);
  const attrs = first?.attributes || first;

  return {
    ...normalized,
    body: getRichBody(attrs),
  };
};

const normalizeSearchText = (value) => {
  if (!value) {
    return "";
  }

  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const tokenizeSearchQuery = (query) => {
  return normalizeSearchText(query)
    .split(" ")
    .map((token) => token.trim())
    .filter(Boolean);
};

const getSearchCandidateText = (item, type) => {
  if (!item) {
    return "";
  }

  const attrs = item?.attributes || item;
  const values = [
    attrs?.title,
    attrs?.headline,
    attrs?.name,
    attrs?.slug,
    attrs?.teaser,
    attrs?.excerpt,
    attrs?.summary,
    attrs?.description,
    attrs?.short_description,
    attrs?.category,
    attrs?.article_type,
    attrs?.sport,
    attrs?.role,
    attrs?.designation,
    attrs?.guestName,
    attrs?.interviewee,
    attrs?.location,
    attrs?.country,
    attrs?.city,
    attrs?.clubName,
    attrs?.type,
    attrs?.meetType,
    type,
  ];

  return normalizeSearchText(values.filter(Boolean).join(" "));
};

const matchesSearchQuery = (item, type, query) => {
  const candidateText = getSearchCandidateText(item, type);
  if (!candidateText) {
    return false;
  }

  const queryTokens = tokenizeSearchQuery(query);
  if (!queryTokens.length) {
    return false;
  }

  return queryTokens.every((token) => {
    if (token.length <= 2) {
      return candidateText.includes(token);
    }

    return candidateText.includes(token) || candidateText.split(" ").some((word) => word.startsWith(token) || token.startsWith(word));
  });
};

const scoreSearchResult = (item, type, query) => {
  const candidateText = getSearchCandidateText(item, type);
  const queryTokens = tokenizeSearchQuery(query);

  if (!candidateText || !queryTokens.length) {
    return 0;
  }

  let score = 0;

  for (const token of queryTokens) {
    if (candidateText.includes(token)) {
      score += token.length >= 4 ? 3 : 2;
      continue;
    }

    const wordMatch = candidateText.split(" ").some((word) => word.startsWith(token) || token.startsWith(word));
    if (wordMatch) {
      score += 1;
    }
  }

  if (candidateText.startsWith(queryTokens[0])) {
    score += 2;
  }

  return score;
};

const dedupeSearchResults = (items) => {
  const seen = new Set();

  return items.filter((item) => {
    const key = item?.slug || item?.id;
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const toSearchResult = (item, type) => {
  if (!item) {
    return null;
  }

  if (type === "sports") {
    const normalized = normalizeArticle(item);
    return {
      id: normalized.id,
      slug: normalized.slug,
      title: normalized.title,
      teaser: normalized.teaser,
      image: normalized.image,
      date: normalized.date,
      href: `/sports/${normalized.slug || normalized.id}`,
      meta: normalized.category || "Sports",
      type,
    };
  }

  if (type === "travel") {
    const normalized = normalizeTravelArticle(item);
    return {
      id: normalized.id,
      slug: normalized.slug,
      title: normalized.title,
      teaser: normalized.teaser,
      image: normalized.image,
      date: normalized.date,
      href: `/travel/${normalized.slug || normalized.id}`,
      meta: normalized.location || normalized.category || "Travel",
      type,
    };
  }

  if (type === "interviews") {
    const normalized = normalizeInterviewArticle(item);
    const meta = [normalized.name, normalized.role].filter(Boolean).join(" • ") || normalized.type || "Interview";

    return {
      id: normalized.id,
      slug: normalized.slug,
      title: normalized.title,
      teaser: normalized.teaser,
      image: normalized.image,
      date: normalized.date,
      href: `/interviews/${normalized.slug || normalized.id}`,
      meta,
      type,
    };
  }

  if (type === "clubs") {
    const normalized = normalizeClubArticle(item);
    const meta = [normalized.city, normalized.country].filter(Boolean).join(" • ") || normalized.level || "Club";

    return {
      id: normalized.id,
      slug: normalized.slug,
      title: normalized.name,
      teaser: normalized.teaser || normalized.short_description,
      image: normalized.image,
      date: normalized.date,
      href: `/clubs/${normalized.slug || normalized.id}`,
      meta,
      type,
    };
  }

  if (type === "meets") {
    const normalized = normalizeMeetArticle(item);
    const meta = [normalized.location, normalized.country].filter(Boolean).join(" • ") || normalized.type || "Meet";

    return {
      id: normalized.id,
      slug: normalized.slug,
      title: normalized.name,
      teaser: normalized.description,
      image: normalized.image,
      date: normalized.date,
      href: `/meets/${normalized.slug || normalized.id}`,
      meta,
      type,
    };
  }

  return null;
};

const searchCollection = async ({ query, endpoints, type, limit = 5 }) => {
  const trimmedQuery = String(query || "").trim();
  if (!trimmedQuery) {
    return [];
  }
};

export const searchSiteContent = async (query, { limit = 5 } = {}) => {
  const trimmedQuery = String(query || "").trim();

  if (!trimmedQuery) {
    return {
      sports: [],
      travel: [],
      interviews: [],
      clubs: [],
      meets: [],
    };
  }

  const results = await Promise.allSettled([
    (async () => {
      try {
        const result = await fetchArticles({ pageSize: 25 });
        const items = Array.isArray(result?.items) ? result.items : [];
        return items
          .filter((item) => matchesSearchQuery(item, "sports", trimmedQuery))
          .map((item) => ({ item, score: scoreSearchResult(item, "sports", trimmedQuery) }))
          .sort((left, right) => right.score - left.score)
          .slice(0, limit)
          .map(({ item }) => toSearchResult(item, "sports"))
          .filter(Boolean);
      } catch (_error) {
        return [];
      }
    })(),
    (async () => {
      try {
        const result = await fetchTravelArticles({ pageSize: 25 });
        const items = Array.isArray(result?.items) ? result.items : [];
        return items
          .filter((item) => matchesSearchQuery(item, "travel", trimmedQuery))
          .map((item) => ({ item, score: scoreSearchResult(item, "travel", trimmedQuery) }))
          .sort((left, right) => right.score - left.score)
          .slice(0, limit)
          .map(({ item }) => toSearchResult(item, "travel"))
          .filter(Boolean);
      } catch (_error) {
        return [];
      }
    })(),
    (async () => {
      try {
        const result = await fetchInterviews({ pageSize: 25 });
        const items = Array.isArray(result?.items) ? result.items : [];
        return items
          .filter((item) => matchesSearchQuery(item, "interviews", trimmedQuery))
          .map((item) => ({ item, score: scoreSearchResult(item, "interviews", trimmedQuery) }))
          .sort((left, right) => right.score - left.score)
          .slice(0, limit)
          .map(({ item }) => toSearchResult(item, "interviews"))
          .filter(Boolean);
      } catch (_error) {
        return [];
      }
    })(),
    (async () => {
      try {
        const items = await fetchClubs({ limit: 50 });
        return Array.isArray(items) ? items
          .filter((item) => matchesSearchQuery(item, "clubs", trimmedQuery))
          .map((item) => ({ item, score: scoreSearchResult(item, "clubs", trimmedQuery) }))
          .sort((left, right) => right.score - left.score)
          .slice(0, limit)
          .map(({ item }) => toSearchResult(item, "clubs"))
          .filter(Boolean) : [];
      } catch (_error) {
        return [];
      }
    })(),
    (async () => {
      try {
        const items = await fetchMeets({ limit: 50 });
        return Array.isArray(items) ? items
          .filter((item) => matchesSearchQuery(item, "meets", trimmedQuery))
          .map((item) => ({ item, score: scoreSearchResult(item, "meets", trimmedQuery) }))
          .sort((left, right) => right.score - left.score)
          .slice(0, limit)
          .map(({ item }) => toSearchResult(item, "meets"))
          .filter(Boolean) : [];
      } catch (_error) {
        return [];
      }
    })(),
  ]);

  const [sports, travel, interviews, clubs, meets] = results.map((result) => (result.status === "fulfilled" ? result.value : []));

  return {
    sports: dedupeSearchResults(sports),
    travel: dedupeSearchResults(travel),
    interviews: dedupeSearchResults(interviews),
    clubs: dedupeSearchResults(clubs),
    meets: dedupeSearchResults(meets),
  };
};
